import { useMemo, useState } from 'react';
import { AlertTriangle, Check, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useAvailableIdentifiers } from '@/hooks/use-channel-identifiers';
import type { AvailableIdentifier, ChannelIdentifierType } from '@/types';
import { helpdeskLabel, helpdeskLabels, resolvesDestinations } from '@/constants/helpdesk-labels';

/** What the picker hands back, shaped to drop straight into a CreateInboxDto. */
export interface PickedDestination {
  provider: string;
  channel_type: ChannelIdentifierType;
  identifier: string;
  label?: string;
}

const IDENTIFIER_TYPES: { value: ChannelIdentifierType; label: string }[] = [
  { value: 'email', label: 'Email address' },
  // Every container-tier destination reports as `brand`, whatever the provider calls it
  // — a Freshdesk product and a Help Scout mailbox both land here.
  { value: 'brand', label: 'Brand or container' },
  { value: 'chat', label: 'Chat widget' },
  { value: 'phone', label: 'Phone number' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'sms', label: 'SMS' }
];

/**
 * Preview only — machine normalises again on write and its answer wins. Shown so the
 * operator is not surprised when what they typed is stored differently.
 *
 * BYTE-IDENTICAL with machine's `normalizeIdentifier` (services/channelDestination.js),
 * which kim-ai-backend and jobs also copy. A preview that disagrees with the real rule is
 * worse than no preview: it tells the operator their phone number will be stored as they
 * typed it, and the lookup at ingest then matches on the digits and misses.
 *
 * Phone and SMS keep only digits and a leading '+', so the same line written
 * '+1 (438) 700-0635' and '+14387000635' is one destination. Handles lose a leading '@'.
 */
const previewNormalized = (
  channelType: ChannelIdentifierType,
  value: string
): string => {
  const trimmed = value.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';

  if (channelType === 'phone' || channelType === 'sms') {
    const digits = trimmed.replace(/[^\d+]/g, '');
    return digits.startsWith('+')
      ? `+${digits.slice(1).replace(/\D/g, '')}`
      : digits;
  }

  const lowered = trimmed.toLowerCase();
  if (channelType === 'instagram' || channelType === 'facebook') {
    return lowered.replace(/^@/, '').slice(0, 255);
  }
  return lowered.slice(0, 255);
};

const sameDestination = (
  a: PickedDestination | null,
  b: AvailableIdentifier
): boolean =>
  a != null && a.provider === b.provider && a.identifier === b.identifier;

interface Props {
  value: PickedDestination | null;
  onChange: (value: PickedDestination | null) => void;
  /**
   * The inbox being edited, when this picker is open on one.
   *
   * A destination already mapped is not selectable, but its OWN inbox has to stay an
   * exception: without it, editing an inbox greyed out the very destination it already
   * holds, so an operator who cleared the picker had no way to put it back.
   */
  ownerInboxId?: number;
}

/**
 * Choose the destination an inbox answers for.
 *
 * Read live from each connected helpdesk. A destination already mapped elsewhere is shown
 * but not selectable: it belongs to exactly one inbox, and moving it means deleting the
 * inbox holding it.
 *
 * The identifier is an opaque provider-side id rather than an address. Gorgias keys every
 * destination on its integration id and Richpanel on a channel id whose format varies; the
 * other six key on their own grouping object — a Zendesk or Kustomer brand, a Zoho
 * department, a Freshdesk product, a Gladly inbox, a Help Scout mailbox. So rows lead with
 * the label a human recognises and show the identifier beneath it in mono.
 *
 * On those six, ONE row covers every address underneath it. That is the point rather than a
 * simplification: a Freshdesk product can hold two addresses on different domains, and an
 * operator who maps one and misses the other sends half a brand's tickets account-wide with
 * nothing on screen to say so. A container cannot be half mapped.
 */
export function DestinationPicker({ value, onChange, ownerInboxId }: Props) {
  const [search, setSearch] = useState('');
  const [manual, setManual] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [manualType, setManualType] = useState<ChannelIdentifierType>('email');
  const [manualProvider, setManualProvider] = useState('');

  const { data, isLoading, error } = useAvailableIdentifiers();

  const rows = useMemo(() => {
    const all = data?.identifiers ?? [];
    const needle = search.trim().toLowerCase();
    const matched = needle
      ? all.filter(
          (r) =>
            r.identifier.toLowerCase().includes(needle) ||
            (r.label ?? '').toLowerCase().includes(needle) ||
            r.provider.toLowerCase().includes(needle)
        )
      : all;
    // Unmapped first, then still-active, then alphabetically — what an operator has left
    // to deal with belongs at the top.
    return [...matched].sort((a, b) => {
      const aMapped = a.mapped_inbox_id != null ? 1 : 0;
      const bMapped = b.mapped_inbox_id != null ? 1 : 0;
      if (aMapped !== bMapped) return aMapped - bMapped;
      if (a.active !== b.active) return a.active ? -1 : 1;
      return (a.label ?? a.identifier).localeCompare(b.label ?? b.identifier);
    });
  }, [data, search]);

  // Grouped by provider because an identifier is only meaningful inside the provider that
  // issued it: Gorgias 12345 and Richpanel 12345 are unrelated.
  const grouped = useMemo(() => {
    const byProvider = new Map<string, AvailableIdentifier[]>();
    for (const r of rows) {
      const list = byProvider.get(r.provider) ?? [];
      list.push(r);
      byProvider.set(r.provider, list);
    }
    return [...byProvider.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  // Only the helpdesks whose tickets carry a destination we can match. The others are
  // account-scoped and take their brand from the inbox, so a mapping saved for one of them
  // would sit there looking configured and never match a ticket.
  const providerOptions = (data?.providers ?? []).filter(resolvesDestinations);
  const unmappableProviders = (data?.providers ?? []).filter(
    (prov) => !resolvesDestinations(prov)
  );

  const setManualDestination = (
    provider: string,
    channelType: ChannelIdentifierType,
    raw: string
  ) => {
    const identifier = raw.trim();
    onChange(
      provider && identifier
        ? { provider, channel_type: channelType, identifier }
        : null
    );
  };

  /**
   * Put the picker back to "no destination".
   *
   * The ONLY way to detach a channel from an inbox. Selecting a row overwrites the
   * choice and the list has no deselect, so before this an operator who mapped the wrong
   * destination could only swap it for another one — never remove it. The clear existed
   * on the wire the whole time (`provider: null`, which InboxDialog sends when the picker
   * comes back empty on an inbox that had one) with nothing in the UI able to ask for it.
   *
   * The manual fields are reset too: leaving them populated would re-fire onChange with
   * the old identifier the moment either select is touched.
   */
  const clearDestination = () => {
    setManualProvider('');
    setManualValue('');
    onChange(null);
  };

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <Label>Destination</Label>
        <div className='flex items-center gap-1'>
          {value && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='text-destructive hover:text-destructive'
              onClick={clearDestination}
            >
              <X className='mr-1 h-3.5 w-3.5' />
              Remove
            </Button>
          )}
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => {
              // The toggle used to clear the destination, which made it a destructive
              // control: flipping it twice on an existing inbox lost the stored
              // destination, and the row could not be re-picked because the list
              // disabled it. Switching how you enter a destination is not the same act
              // as removing one, so the value is carried across and the manual fields
              // are seeded from it.
              setManual((m) => {
                if (!m && value) {
                  setManualProvider(value.provider);
                  setManualType(value.channel_type);
                  setManualValue(value.identifier);
                }
                return !m;
              });
            }}
          >
            {manual ? 'Pick from the list' : 'Enter one manually'}
          </Button>
        </div>
      </div>

      {/* What is mapped right now, stated plainly beside the control that removes it.
          Without it "Remove" is an unlabelled act: the list scrolls, the selected row is
          marked only by a tick, and in manual mode there is no row at all. */}
      {value ? (
        <p className='text-xs text-muted-foreground'>
          Currently answering for{' '}
          <span className='font-medium text-foreground'>
            {value.label || value.identifier}
          </span>{' '}
          on {helpdeskLabel(value.provider)}.
        </p>
      ) : null}

      {/* Only for providers that CAN be mapped. Telling an operator to type a Zendesk
          identifier by hand invites a mapping that is saved and then never matched. */}
      {data?.providers_without_discovery?.filter(resolvesDestinations).length ? (
        <p className='text-xs text-muted-foreground'>
          No channel list yet for{' '}
          <span className='font-medium text-foreground'>
            {helpdeskLabels(
              data.providers_without_discovery.filter(resolvesDestinations)
            )}
          </span>
          . Enter those identifiers manually.
        </p>
      ) : null}

      {unmappableProviders.length ? (
        <p className='text-xs text-muted-foreground'>
          <span className='font-medium text-foreground'>
            {helpdeskLabels(unmappableProviders)}
          </span>{' '}
          {unmappableProviders.length === 1 ? 'is' : 'are'} connected once for the whole
          account and{' '}
          {unmappableProviders.length === 1 ? 'puts' : 'put'} no destination on a ticket, so{' '}
          {unmappableProviders.length === 1 ? 'it cannot' : 'they cannot'} be mapped here.
          Their tickets take the brand on the inbox created when the helpdesk was connected.
        </p>
      ) : null}

      {data?.provider_lookup_failed?.length ? (
        <div className='flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5 text-xs'>
          <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500' />
          <span className='text-muted-foreground'>
            Could not reach{' '}
            <span className='font-medium text-foreground'>
              {helpdeskLabels(data.provider_lookup_failed)}
            </span>
            . Their credentials may need reconnecting, so this list is
            incomplete.
          </span>
        </div>
      ) : null}

      {manual ? (
        <div className='space-y-3'>
          <div className='space-y-1.5'>
            <Label htmlFor='id-provider'>Provider</Label>
            <Select
              value={manualProvider}
              onValueChange={(v) => {
                setManualProvider(v);
                setManualDestination(v, manualType, manualValue);
              }}
            >
              <SelectTrigger id='id-provider' className='w-full'>
                <SelectValue placeholder='Which helpdesk delivers this?' />
              </SelectTrigger>
              <SelectContent>
                {providerOptions.map((prov) => (
                  <SelectItem key={prov} value={prov}>
                    {helpdeskLabel(prov)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              An identifier only means something alongside the provider that
              issued it.
            </p>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='id-type'>Type</Label>
            <Select
              value={manualType}
              onValueChange={(v) => {
                const next = v as ChannelIdentifierType;
                setManualType(next);
                setManualDestination(manualProvider, next, manualValue);
              }}
            >
              <SelectTrigger id='id-type' className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IDENTIFIER_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='id-value'>Identifier</Label>
            <Input
              id='id-value'
              value={manualValue}
              onChange={(e) => {
                setManualValue(e.target.value);
                setManualDestination(manualProvider, manualType, e.target.value);
              }}
              placeholder='e.g. the provider’s integration, brand or product id'
            />
            {manualValue.trim() && (
              <p className='text-xs text-muted-foreground'>
                Will be stored as{' '}
                <span className='font-mono'>
                  {previewNormalized(manualType, manualValue)}
                </span>
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className='space-y-2'>
          <div className='relative'>
            <Search className='absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
            <Input
              className='pl-8'
              placeholder='Search destinations…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className='max-h-64 overflow-y-auto rounded-md border'>
            {isLoading ? (
              <div className='space-y-2 p-3'>
                <Skeleton className='h-8 w-full' />
                <Skeleton className='h-8 w-full' />
              </div>
            ) : error ? (
              <p className='p-4 text-center text-sm text-destructive'>
                {error instanceof Error
                  ? error.message
                  : 'Could not load destinations.'}
              </p>
            ) : rows.length === 0 ? (
              <p className='p-4 text-center text-sm text-muted-foreground'>
                Nothing to pick from. Use “enter one manually” above.
              </p>
            ) : (
              grouped.map(([provider, items]) => (
                <div key={provider}>
                  <div className='sticky top-0 z-10 border-b bg-muted/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur'>
                    {helpdeskLabel(provider)}
                  </div>
                  {items.map((r) => {
                    // Mapped ELSEWHERE. Its own inbox is excepted, or editing an inbox
                    // disables the row it already points at and the destination cannot be
                    // restored once the picker has been cleared.
                    const alreadyMapped =
                      r.mapped_inbox_id != null && r.mapped_inbox_id !== ownerInboxId;
                    const isSelected = sameDestination(value, r);
                    return (
                      <button
                        key={`${r.provider}:${r.channel_type}:${r.identifier}`}
                        type='button'
                        disabled={alreadyMapped}
                        onClick={() =>
                          onChange({
                            provider: r.provider,
                            channel_type: r.channel_type,
                            identifier: r.identifier,
                            ...(r.label ? { label: r.label } : {})
                          })
                        }
                        className='flex w-full items-center gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50'
                      >
                        <span className='w-4 shrink-0'>
                          {isSelected && <Check className='h-3.5 w-3.5' />}
                        </span>
                        <span className='min-w-0 flex-1'>
                          <span className='block truncate font-medium'>
                            {r.label || r.identifier}
                          </span>
                          {r.label && (
                            <span className='block truncate font-mono text-xs text-muted-foreground'>
                              {r.identifier}
                            </span>
                          )}
                        </span>
                        <Badge variant='outline' className='shrink-0'>
                          {r.channel_type}
                        </Badge>
                        {!r.active && (
                          <Badge variant='outline' className='shrink-0'>
                            inactive
                          </Badge>
                        )}
                        {alreadyMapped && (
                          <Badge variant='secondary' className='shrink-0'>
                            mapped
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
